import type { CartTargetingFe } from "@intelligems/ig-types/fe-only/cart-targeting";
import type { CreateAudienceDto } from "@intelligems/ig-types/dtos/audiences";
import type {
  PostPurchaseTimerVariables,
  PostPurchaseUpsellBlockVariables,
  PostPurchaseUpsellCheckoutBlockDto,
} from "@intelligems/ig-types/dtos/checkout-blocks";
import type {
  UpsellCompareAtPriceDisplay,
  UpsellDiscountType,
  UpsellProductRulesAction,
} from "@intelligems/ig-types/dtos/upsell-product-rules";

export const generateInitialAudience = (options: {
  id: string;
  defaultCurrency?: string;
}): CreateAudienceDto => ({
  id: options.id,
  enabled: true,
  excludeCurrency: {
    exclude: Boolean(options.defaultCurrency),
    currency: options.defaultCurrency || null,
    country: undefined,
    region: undefined,
    city: undefined,
  },
  wholesale: false,
  audienceType: "common",
  elseAction: null,
  elseVariationId: null,
  elseExcludeFromAnalytics: false,
  filters: [],
  evaluationFrequency: null,
});

/**
 * Defaults for a new post-purchase upsell and its first product rule.
 *
 * The dashboard's create flow and backend scripts that create post-purchase
 * upsells for merchants both build from these, so a block made by a script is
 * the same block a merchant gets by clicking create. Keep them pure: callers
 * pass in ids and names, and nothing here touches the DB, org context or React.
 */
export const POST_PURCHASE_OFFER_TIMEOUT_DEFAULT_MINUTES = 60;

/** How many products a new upsell starts at. */
export const POST_PURCHASE_DEFAULT_PRODUCT_LIMIT = 3;

// The discount a post-purchase offer's rules carry:
export const postPurchaseRuleDiscount = (
  variables: Pick<
    PostPurchaseUpsellBlockVariables,
    "discountType" | "discountValue" | "compareAtPriceDisplay"
  >
): {
  discountType: UpsellDiscountType;
  discountValue: number | null;
  compareAtPriceDisplay: UpsellCompareAtPriceDisplay;
} => ({
  discountType: variables.discountType,
  discountValue: variables.discountValue ?? null,
  compareAtPriceDisplay: variables.compareAtPriceDisplay,
});

export const defaultTimerCheckout: PostPurchaseTimerVariables = {
  timerMode: "perVisitor",
  durationMinutes: 15,
  resetIntervalUnit: "day",
  countdownText: "Offer expires in #countdownTextTimeRemaining",
  expiredBehavior: "showText",
  expiredText: "This offer has expired",
  timerTemplate: "textCountdown",
  headline: "",
  showDays: false,
  showHours: true,
  showMinutes: true,
  showSeconds: true,
};

export const generateInitialPostPurchaseVariables =
  (): PostPurchaseUpsellCheckoutBlockDto["variables"] => ({
    headerText: "",
    discountType: "none",
    discountValue: undefined,
    compareAtPriceDisplay: "none",
    imageAspectRatio: "1:1",
    excludeOutOfStock: true,
    excludeItemsInCart: true,
    maxProductLimit: POST_PURCHASE_DEFAULT_PRODUCT_LIMIT,
    bannerEnabled: true,
    bannerTitle: "It's not too late to add this to your order",
    bannerSubtitle: "Don't miss out on these picks for you",
    headingLevel: "auto",
    compareAtPriceEnabled: false,
    descriptionEnabled: false,
    primaryButtonText: "Accept offer",
    secondaryButtonEnabled: true,
    secondaryButtonText: "Decline this offer",
    quantitySelectorEnabled: false,
    quantityLabel: "Quantity",
    maxQuantity: 3,
    maxQuantityType: "noMaximum",
    moneyLineEnabled: true,
    moneyLineTaxes: true,
    moneyLineSubtotal: true,
    moneyLineShipping: true,
    offerTimeout: POST_PURCHASE_OFFER_TIMEOUT_DEFAULT_MINUTES,
    countdownTimerEnabled: false,
    timerCheckout: { ...defaultTimerCheckout },
  });

export const generateInitialPostPurchaseStyles =
  (): PostPurchaseUpsellCheckoutBlockDto["styles"] => ({
    typography: {
      productTitle: { fontSize: "base", fontStyle: "normal" },
      price: { fontSize: "base", fontStyle: "normal" },
      primaryButton: { fontSize: "base", fontStyle: "bold" },
    },
    backgroundColors: {
      primaryButton: "base",
      banner: "secondary",
      expired: "secondary",
    },
    colors: {
      productText: "info",
      priceText: "info",
      primaryButtonText: "info",
      expiredText: "critical",
    },
    appearance: {
      pricingEmphasized: true,
      descriptionSubdued: true,
      separatorWidth: "tight",
      secondaryButtonStyle: "default",
      bannerBorderStyle: "none",
      bannerSpacing: "tight",
      bannerTextAlign: "center",
      imageFit: "cover",
      imageBorder: false,
    },
  });

export const generateInitialPostPurchaseBlock = (options: {
  id: string;
  variationId: string;
  name: string;
}): PostPurchaseUpsellCheckoutBlockDto => ({
  id: options.id,
  variationId: options.variationId,
  name: options.name,
  locationId: "ig-post-purchase",
  blockType: "postPurchaseUpsell",
  variables: generateInitialPostPurchaseVariables(),
  styles: generateInitialPostPurchaseStyles(),
  upsellProductRules: [],
});

/** A new upsell rule matches any cart. */
export const generateDefaultUpsellCartTargetingExpression =
  (): CartTargetingFe["expression"] => [
    {
      query: {
        type: "anything",
        key: "anything",
        filter: "includes",
        value: undefined,
      },
    },
  ];

/** A new upsell rule offers Shopify's product recommendations. */
export const generateDefaultUpsellProductRuleAction = (discount?: {
  discountType?: UpsellDiscountType | null;
  discountValue?: number | null;
  compareAtPriceDisplay?: UpsellCompareAtPriceDisplay;
}): UpsellProductRulesAction => ({
  expression: [
    {
      query: {
        type: "shopifyRecommendations",
        key: "shopifyRecommendations",
      },
    },
  ],
  discountType: discount?.discountType ?? "none",
  discountValue: discount?.discountValue ?? null,
  compareAtPriceDisplay: discount?.compareAtPriceDisplay ?? "none",
  onAccept: null,
  onDecline: null,
});
